import { NestApiType, nestCall } from '@/composables/nestApi';
import type {
  ImportAdapter,
  ImportContext,
  ImportWizardState,
  InitImportResponse,
  DescribeFileResponse,
  SelectableItem,
  ValidateFileResponse,
  ImportStepConfig,
} from '../types';
import { createAdapter } from './base.adapter';

// =============================================================================
// ZIP IMPORT ADAPTER BASE
// =============================================================================

/**
 * Configuration for creating a ZIP-based import adapter
 * Used for campaign_automation, campaign_bulk, and template imports
 */
export interface ZipImportAdapterConfig {
  /** Unique type identifier (e.g., 'campaign_automation', 'campaign_bulk', 'template') */
  type: string;

  /** API endpoint for describe-file call (defaults to '/import/describe-file') */
  describeFileEndpoint?: string;

  /** Error message when no items found in file */
  emptyFileErrorKey?: string;

  alternativeType?: string;
}

// =============================================================================
// SHARED FUNCTIONS
// =============================================================================

/**
 * Convert describe-file API response to SelectableItem array
 * All items are selected by default
 */
export function convertToSelectableItems(response: DescribeFileResponse): SelectableItem[] {
  return Object.entries(response.items).map(([id, name]) => {
    const rawPreviews = response.previews?.[id];
    // Normalize previews: support both old format (string) and new format (string[])
    let previews: string[] | undefined;
    if (Array.isArray(rawPreviews)) {
      previews = rawPreviews;
    } else if (typeof rawPreviews === 'string') {
      previews = [rawPreviews];
    }
    const htmlPreviewCount = response.htmlPreviewCounts?.[id] || 0;
    return {
      id,
      name,
      selected: true,
      preview: previews?.[0] || undefined,
      previews,
      htmlPreviewCount: htmlPreviewCount > 0 ? htmlPreviewCount : undefined,
      htmlPreviewSessionId: htmlPreviewCount > 0 ? (response.htmlPreviewSessionId || undefined) : undefined,
    };
  });
}

/**
 * Get only selected items from the state
 */
export function getSelectedItems(state: ImportWizardState): SelectableItem[] {
  return state.selectedItems.filter((item) => item.selected);
}

/**
 * Get selected item IDs from the state
 */
export function getSelectedItemIds(state: ImportWizardState): string[] {
  return getSelectedItems(state).map((item) => item.id);
}

// =============================================================================
// ZIP IMPORT STEPS (shared by all ZIP-based adapters)
// =============================================================================

/**
 * Standard steps for ZIP-based imports:
 * 1. File select (ZIP only, no copy-paste)
 * 2. Item selector (select which items to import)
 * 3. Preview (confirm import)
 */
export const ZIP_IMPORT_STEPS: ImportStepConfig[] = [
  {
    id: 'file_select',
    type: 'file_select' as const,
    descriptionKey: 'import.wizard.steps.select',
    allowCopyPaste: false,
    accept: '.zip',
  },
  {
    id: 'item_selector',
    type: 'item_selector' as const,
    descriptionKey: 'import.wizard.steps.selectItems',
  },
  {
    id: 'preview',
    type: 'preview' as const,
    descriptionKey: 'import.wizard.steps.preview',
  },
];

// =============================================================================
// DESCRIBE FILE API CALL
// =============================================================================

/**
 * Call describe-file API to get items from the uploaded zip file
 *
 * @param file - The uploaded zip file
 * @param config - Adapter configuration
 * @returns DescribeFileResponse with items or error
 */
async function describeFile(
  file: File,
  config: ZipImportAdapterConfig,
): Promise<DescribeFileResponse> {
  const formData = new FormData();
  formData.append('exportFile', file);

  const endpoint = config.describeFileEndpoint ?? '/job/import/describe-file';

  const dataArguments: Record<string, string> = {
    'import-type': config.type,
  };
  if (config.alternativeType) {
    dataArguments['alternative-type'] = config.alternativeType;
  }

  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    endpoint,
    dataArguments,
    '',
    formData,
  );

  if (response?.error) {
    const errorMessage = response.error.message || 'Failed to describe file';
    return {
      items: {},
      error: errorMessage,
    };
  }

  return {
    items: response?.result || {},
    previews: response?.previews || {},
    htmlPreviewSessionId: response?.htmlPreviewSessionId || null,
    htmlPreviewCounts: response?.htmlPreviewCounts || {},
    hasObsoleteTemplates: response?.hasObsoleteTemplates || false,
  };
}

// =============================================================================
// INIT IMPORT API CALL
// =============================================================================

/**
 * Initialize import job via API
 * Sends selected item IDs to the server
 * If ctx.params is provided, all params are automatically added to initData
 *
 * @param importType - Type of import
 * @param ctx - Import context (may contain additional params)
 * @param state - Current wizard state
 * @param fileType - Type of file being uploaded
 * @returns InitImportResponse with upload URL
 */
async function initZipImport(
  importType: string,
  ctx: ImportContext,
  state: ImportWizardState,
  fileType: string,
): Promise<InitImportResponse> {
  const selectedIds = getSelectedItemIds(state);

  const initData: Record<string, unknown> = {
    file_type: fileType,
    selected_ids: selectedIds,
    // Automatically add all params from context if available
    ...ctx.params,
  };

  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    '/job/import/init',
    {
      'import-type': importType,
    },
    '',
    initData,
  );

  if (response?.error) {
    throw new Error(response.error.message || 'Failed to initialize import');
  }

  if (!response?.data) {
    throw new Error('Invalid response from server');
  }

  return response.data;
}

// =============================================================================
// ADAPTER FACTORY
// =============================================================================

/**
 * Create a ZIP-based import adapter
 * Reduces boilerplate for campaign_automation, campaign_bulk, and template adapters
 *
 * @param config - Adapter configuration
 * @returns Configured ImportAdapter
 */
export function createZipImportAdapter(config: ZipImportAdapterConfig): ImportAdapter {
  const errorKey = config.emptyFileErrorKey ?? 'import.itemSelector.noItemsFound';

  return createAdapter({
    type: config.type,

    steps: ZIP_IMPORT_STEPS,

    validateFile: async (_ctx: ImportContext, file: File): Promise<ValidateFileResponse> => {
      const response = await describeFile(file, config);

      if (response.error) {
        return {
          success: false,
          error: response.error,
        };
      }

      const items = convertToSelectableItems(response);

      if (items.length === 0) {
        return {
          success: false,
          error: errorKey,
        };
      }

      return {
        success: true,
        items,
        hasObsoleteTemplates: response.hasObsoleteTemplates,
      };
    },

    initImport: (
      ctx: ImportContext,
      state: ImportWizardState,
      fileType: string,
    ): Promise<InitImportResponse> => initZipImport(config.type, ctx, state, fileType),

    getPreviewData: (state: ImportWizardState) => {
      const selectedItems = getSelectedItems(state);
      return {
        totalItems: selectedItems.length,
        items: selectedItems,
        fileName: state.fileData.file?.name ?? '',
      };
    },

    // Custom canProceed for item selector - must have at least one selected item
    canProceed: (stepIndex: number, state: ImportWizardState): boolean => {
      const step = ZIP_IMPORT_STEPS[stepIndex];
      if (!step) return false;

      switch (step.type) {
        case 'file_select':
          return state.fileData.file !== null;

        case 'item_selector':
          // Must have at least one item with selected: true
          return state.selectedItems.some((item) => item.selected);

        case 'preview':
          return true;

        default:
          return false;
      }
    },
  });
}

// =============================================================================
// EXPORTS
// =============================================================================

export {
  describeFile,
  initZipImport,
};
